import { fetchResolveBrowseUrl, type ResolveBrowseUrlApiResponse } from '@/redux/api/browseUrl';
import {
    type BrowseDimensionPair,
    browsePathHasTildeMultiValue,
    buildBrowseCategoryPath,
    mapBrowsePairsToSearchOptionIds,
    parseBrowseTail,
    sortBrowsePairsCanonical,
} from '@/utils/browsePath/browsePath';
import {
    getLegacyQuadFacetedCanonicalPath,
    isLegacyFacetedNumericTailBrowsePath,
    isUnknownSlugOnlyBrowsePath,
    stripsLegacyNumericTailToOwnTopSlug,
} from '@/utils/browsePath/legacyQuadBrowseRedirect';
import { getTopCategoryIdFromSlug } from '@/utils/browsePath/resolveCategorySlug';

import { mapResolvedCategoryIdsToBrowseFacetOptions } from './queryBuilders';
import { CategoryRouteContext } from './routeContext';

type ResolveBrowseInputsArgs = {
    deployment: string;
    locationSearch: string;
    routeContext: CategoryRouteContext;
    searchFacets: any;
    stateAfterFacets: any;
};

export type BrowseResolutionResult = {
    baseCategoryIds: number[];
    browseFacetOptionIds: Record<string, string[]> | undefined;
    browsePathPairsForPostSearchRemap: BrowseDimensionPair[];
    redirectUrl: string | null;
    shouldAttemptPostSearchBrowseRemap: boolean;
    /**
     * Set only when redirecting to slug-only would actually render (slug is a known top category).
     * For unknown sub-category slugs the slug-only page also has no top-cat id, so falling back there
     * just loops to the 404 page — leave this `null` and let the loader keyword-search instead.
     */
    slugOnlyPathForInvalidBrowsePath: string | null;
    /** Slug to keyword-search when no `baseCategoryIds` could be resolved (sub-category browse). */
    unresolvedBrowseSlugForKeywordSearch: string | null;
};

const hasAnyFacetIds = (facetOptionIds: Record<string, string[]> | undefined) =>
    Boolean(facetOptionIds && Object.values(facetOptionIds).some((ids) => Array.isArray(ids) && ids.length > 0));

/**
 * `search.facets` are global to the last search response; `runPreloadPhase` does not load facets for the incoming URL.
 * Using them to map `/c/{slug}/origin/african/` while `lastBrowseCategorySlug` still refers to a previous category
 * resolves slugs against the wrong taxonomy (or overwrites resolver facet ids during merge).
 */
const searchFacetsApplyToBrowseSlug = (
    stateAfterFacets: { browsePath?: { lastBrowseCategorySlug: string | null } },
    browseCategorySlug: string | undefined
): boolean =>
    Boolean(
        browseCategorySlug &&
        stateAfterFacets?.browsePath?.lastBrowseCategorySlug !== null &&
        stateAfterFacets?.browsePath?.lastBrowseCategorySlug === browseCategorySlug
    );

export const resolveBrowseInputs = async ({
    deployment,
    locationSearch,
    routeContext,
    searchFacets,
    stateAfterFacets,
}: ResolveBrowseInputsArgs): Promise<BrowseResolutionResult> => {
    const {
        browseParsed,
        browseSplat,
        hasRegularLegacyCategory,
        isBrowseSlugRoute,
        isCombinationCategory,
        legacyCategoryId,
        legacyCategoryId2,
        legacyPathParsed,
        pathname,
    } = routeContext;

    const resolveBrowseUrlSafe = async (path: string): Promise<ResolveBrowseUrlApiResponse> => {
        try {
            return await fetchResolveBrowseUrl({
                deployment,
                pathname: path,
                search: '',
            });
        } catch {
            return { error: true };
        }
    };

    const redirectPayloadToResult = (
        redirectPathResolved: string
    ): Pick<
        BrowseResolutionResult,
        'baseCategoryIds' | 'browseFacetOptionIds' | 'browsePathPairsForPostSearchRemap' | 'redirectUrl'
    > => {
        const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
        return {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            redirectUrl: `${redirectPathResolved}${querySuffix}`,
        };
    };

    let baseCategoryIds: number[] = [];
    let browseFacetOptionIds: Record<string, string[]> | undefined;
    let shouldAttemptPostSearchBrowseRemap = false;
    let browsePathPairsForPostSearchRemap: BrowseDimensionPair[] = [];
    let slugOnlyPathForInvalidBrowsePath: string | null = null;

    if (legacyPathParsed) {
        return {
            baseCategoryIds: [Number(legacyPathParsed.numericCategoryId)],
            browseFacetOptionIds: undefined,
            browsePathPairsForPostSearchRemap: [],
            redirectUrl: null,
            shouldAttemptPostSearchBrowseRemap: false,
            slugOnlyPathForInvalidBrowsePath: null,
            unresolvedBrowseSlugForKeywordSearch: null,
        };
    }

    const legacyFacetCanonicalPath = getLegacyQuadFacetedCanonicalPath(pathname);
    if (legacyFacetCanonicalPath !== null) {
        const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
        return {
            baseCategoryIds: [],
            browseFacetOptionIds: undefined,
            browsePathPairsForPostSearchRemap: [],
            redirectUrl: `${legacyFacetCanonicalPath}${querySuffix}`,
            shouldAttemptPostSearchBrowseRemap: false,
            slugOnlyPathForInvalidBrowsePath: null,
            unresolvedBrowseSlugForKeywordSearch: null,
        };
    }

    if (isBrowseSlugRoute && routeContext.params?.browseCategorySlug) {
        const slugOnlyPath = browseParsed
            ? buildBrowseCategoryPath(browseParsed.categorySlug, [], { base: browseParsed.base })
            : pathname.startsWith('/price-guide/')
              ? `/price-guide/${routeContext.params.browseCategorySlug}/`
              : pathname.startsWith('/similar-items/')
                ? `/similar-items/${routeContext.params.browseCategorySlug}/`
                : `/c/${routeContext.params.browseCategorySlug}/`;
        // Slug-only path is always a safe fallback now: known top-cats resolve to their id, unknown
        // sub-cats render via keyword search at the loader level. (Previously this was guarded against
        // unknown sub-cats to avoid an Oops loop; that loop is fixed.)
        slugOnlyPathForInvalidBrowsePath = slugOnlyPath;

        const pathPairsForBrowse = browseParsed?.pairs?.length > 0 ? browseParsed.pairs : parseBrowseTail(browseSplat);
        const hasMalformedBrowseSplat = Boolean(
            browseSplat && browseSplat.split('/').filter(Boolean).length > 0 && pathPairsForBrowse.length === 0
        );
        const rawFacetsForPathMapping = searchFacetsApplyToBrowseSlug(
            stateAfterFacets,
            routeContext.params.browseCategorySlug
        )
            ? searchFacets
            : [];
        const pathMappedFacets = mapBrowsePairsToSearchOptionIds(
            sortBrowsePairsCanonical(pathPairsForBrowse),
            rawFacetsForPathMapping
        );
        const pathMappedHasIds = hasAnyFacetIds(pathMappedFacets);

        const knownTopCategoryIdFromSlug = getTopCategoryIdFromSlug(routeContext.params.browseCategorySlug);
        if (knownTopCategoryIdFromSlug !== null) {
            baseCategoryIds = [knownTopCategoryIdFromSlug];
            if (hasMalformedBrowseSplat) {
                /**
                 * Indexed legacy URLs (`/c/{top}/{ambiguous}/`, price-guide/similar-items analogs) often mismatch
                 * strict browse dimensions. Try category-api `resolve-browse-url` before stripping to slug-only —
                 * use `redirectPath`, `categoryIds`, and `facetOptionsByFacetId` when present.
                 */
                const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
                const attemptMalformedResolver = !browsePathHasTildeMultiValue(pathname);
                if (!attemptMalformedResolver) {
                    return {
                        baseCategoryIds,
                        browseFacetOptionIds,
                        browsePathPairsForPostSearchRemap,
                        redirectUrl: `${slugOnlyPath}${querySuffix}`,
                        shouldAttemptPostSearchBrowseRemap,
                        slugOnlyPathForInvalidBrowsePath,
                        unresolvedBrowseSlugForKeywordSearch: null,
                    };
                }
                const malformedResolved = await resolveBrowseUrlSafe(pathname);
                const malformedRedirectPath = malformedResolved.payload?.redirectPath;
                if (
                    !malformedResolved.error &&
                    typeof malformedRedirectPath === 'string' &&
                    malformedRedirectPath.length > 0 &&
                    malformedRedirectPath !== pathname &&
                    !isUnknownSlugOnlyBrowsePath(malformedRedirectPath) &&
                    !stripsLegacyNumericTailToOwnTopSlug(pathname, malformedRedirectPath)
                ) {
                    return {
                        ...redirectPayloadToResult(malformedRedirectPath),
                        shouldAttemptPostSearchBrowseRemap,
                        slugOnlyPathForInvalidBrowsePath,
                        unresolvedBrowseSlugForKeywordSearch: null,
                    };
                }
                const malformedResolverClarified =
                    Boolean(malformedResolved.payload?.categoryIds?.length) ||
                    hasAnyFacetIds(malformedResolved.payload?.facetOptionsByFacetId);
                if (!malformedResolverClarified) {
                    /* Legacy numeric-tail URLs (e.g. `/c/art/lithograph/1/25811/`) often can't be auto-
                     * canonicalized and the resolver can't clarify them either. Fall through and render
                     * the top-cat instead of bouncing to slug-only, which loses meaningful context. */
                    if (!isLegacyFacetedNumericTailBrowsePath(pathname)) {
                        return {
                            baseCategoryIds,
                            browseFacetOptionIds,
                            browsePathPairsForPostSearchRemap,
                            redirectUrl: `${slugOnlyPath}${querySuffix}`,
                            shouldAttemptPostSearchBrowseRemap,
                            slugOnlyPathForInvalidBrowsePath,
                            unresolvedBrowseSlugForKeywordSearch: null,
                        };
                    }
                }
                if (!malformedResolved.error && malformedResolved.payload?.categoryIds?.length) {
                    baseCategoryIds = malformedResolved.payload.categoryIds;
                }
                if (!malformedResolved.error && malformedResolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = malformedResolved.payload.facetOptionsByFacetId;
                }
            }

            if (pathPairsForBrowse.length > 0 && !pathMappedHasIds && !browsePathHasTildeMultiValue(pathname)) {
                const resolved = await resolveBrowseUrlSafe(pathname);
                const resolvedRedirectPath = resolved.payload?.redirectPath;
                if (
                    !resolved.error &&
                    typeof resolvedRedirectPath === 'string' &&
                    resolvedRedirectPath.length > 0 &&
                    resolvedRedirectPath !== pathname &&
                    !isUnknownSlugOnlyBrowsePath(resolvedRedirectPath) &&
                    !stripsLegacyNumericTailToOwnTopSlug(pathname, resolvedRedirectPath)
                ) {
                    return {
                        ...redirectPayloadToResult(resolvedRedirectPath),
                        shouldAttemptPostSearchBrowseRemap,
                        slugOnlyPathForInvalidBrowsePath,
                        unresolvedBrowseSlugForKeywordSearch: null,
                    };
                }

                if (!resolved.error && resolved.payload?.categoryIds?.length) {
                    baseCategoryIds = resolved.payload.categoryIds;
                    if (!resolved.payload.facetOptionsByFacetId) {
                        const categoryIdBackedFallback = mapResolvedCategoryIdsToBrowseFacetOptions(
                            pathPairsForBrowse,
                            resolved.payload.categoryIds
                        );
                        if (categoryIdBackedFallback) {
                            browseFacetOptionIds = categoryIdBackedFallback;
                        }
                    }
                }

                if (!resolved.error && resolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = resolved.payload.facetOptionsByFacetId;
                }
            }
        } else {
            if (!browsePathHasTildeMultiValue(pathname)) {
                const resolved = await resolveBrowseUrlSafe(pathname);
                const resolvedRedirectUnknown = resolved.payload?.redirectPath;
                if (
                    !resolved.error &&
                    typeof resolvedRedirectUnknown === 'string' &&
                    resolvedRedirectUnknown.length > 0 &&
                    resolvedRedirectUnknown !== pathname &&
                    !isUnknownSlugOnlyBrowsePath(resolvedRedirectUnknown) &&
                    !stripsLegacyNumericTailToOwnTopSlug(pathname, resolvedRedirectUnknown)
                ) {
                    return {
                        ...redirectPayloadToResult(resolvedRedirectUnknown),
                        shouldAttemptPostSearchBrowseRemap,
                        slugOnlyPathForInvalidBrowsePath,
                        unresolvedBrowseSlugForKeywordSearch: null,
                    };
                }
                if (!resolved.error && resolved.payload) {
                    if (resolved.payload.categoryIds?.length) {
                        baseCategoryIds = resolved.payload.categoryIds;
                    }
                    if (resolved.payload.facetOptionsByFacetId) {
                        browseFacetOptionIds = resolved.payload.facetOptionsByFacetId;
                    }
                }
            }

            if (!baseCategoryIds.length && slugOnlyPath !== pathname) {
                const slugResolved = await resolveBrowseUrlSafe(slugOnlyPath);
                if (!slugResolved.error && slugResolved.payload?.categoryIds?.length) {
                    baseCategoryIds = slugResolved.payload.categoryIds;
                }
                if (!browseFacetOptionIds && !slugResolved.error && slugResolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = slugResolved.payload.facetOptionsByFacetId;
                }
            }
        }

        if (!baseCategoryIds.length) {
            const topId = getTopCategoryIdFromSlug(routeContext.params.browseCategorySlug);
            if (topId) {
                baseCategoryIds = [topId];
            }
        }

        if (!baseCategoryIds.length && routeContext.params.browseCategorySlug) {
            const { baseCategoryIds: prevIds, lastBrowseCategorySlug } = stateAfterFacets.browsePath;
            if (
                prevIds.length > 0 &&
                lastBrowseCategorySlug !== null &&
                lastBrowseCategorySlug === routeContext.params.browseCategorySlug
            ) {
                baseCategoryIds = [...prevIds];
            }
        }

        const apiHasAnyFacetIds = hasAnyFacetIds(browseFacetOptionIds);
        if (!apiHasAnyFacetIds) {
            browseFacetOptionIds = pathMappedFacets;
        } else if (pathMappedHasIds) {
            browseFacetOptionIds = { ...browseFacetOptionIds, ...pathMappedFacets };
        }

        if (!hasAnyFacetIds(browseFacetOptionIds) && pathPairsForBrowse.length > 0) {
            shouldAttemptPostSearchBrowseRemap = true;
            browsePathPairsForPostSearchRemap = sortBrowsePairsCanonical(pathPairsForBrowse);
        }

        const unresolvedBrowseSlugForKeywordSearch =
            baseCategoryIds.length === 0 ? routeContext.params.browseCategorySlug : null;

        return {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            redirectUrl: null,
            shouldAttemptPostSearchBrowseRemap,
            slugOnlyPathForInvalidBrowsePath,
            unresolvedBrowseSlugForKeywordSearch,
        };
    }

    /**
     * `/c/:name/:name2/:id/:id/` once meant “facet slug + breadcrumb ids”. some indexed digit routes still match,
     * so we ask `resolve-browse-url` for a canonical `/c/.../{dimension}/{slug}/` redirect before concatenated combos.
     */
    if (!isBrowseSlugRoute && !legacyPathParsed && isCombinationCategory && !browsePathHasTildeMultiValue(pathname)) {
        const combinationResolved = await resolveBrowseUrlSafe(pathname);
        const combinationRedirect = combinationResolved.payload?.redirectPath;
        if (
            !combinationResolved.error &&
            typeof combinationRedirect === 'string' &&
            combinationRedirect.length > 0 &&
            combinationRedirect !== pathname
        ) {
            return {
                ...redirectPayloadToResult(combinationRedirect),
                shouldAttemptPostSearchBrowseRemap,
                slugOnlyPathForInvalidBrowsePath: null,
                unresolvedBrowseSlugForKeywordSearch: null,
            };
        }
    }

    if (isCombinationCategory) {
        baseCategoryIds = [legacyCategoryId, legacyCategoryId2].filter((id) => !Number.isNaN(id));
    } else if (hasRegularLegacyCategory) {
        baseCategoryIds = [legacyCategoryId];
    }

    return {
        baseCategoryIds,
        browseFacetOptionIds,
        browsePathPairsForPostSearchRemap,
        redirectUrl: null,
        shouldAttemptPostSearchBrowseRemap,
        slugOnlyPathForInvalidBrowsePath,
        unresolvedBrowseSlugForKeywordSearch: null,
    };
};
